<template>
  <!-- 
    CardRow Component
    =================
    Single Responsibility: Manage a horizontal row of Pokemon cards
    
    This component:
    - Manages positioning of cards within a row
    - Uses CSS Grid for responsive layout
    - Handles card loading orchestration
    - Emits events when all cards in row are loaded
    - Applies row-level animations
  -->
  <div 
    class="card-row"
    :class="{ 
      'is-loaded': allCardsLoaded,
      'is-visible': isVisible 
    }"
    :style="rowStyles"
    ref="rowElement"
  >
    <!-- 
      Render each card in the row
      Each card handles its own loading state
    -->
    <PokemonCard
      v-for="(card, index) in cards"
      :key="`${rowId}-card-${index}`"
      :image-src="card.src"
      :image-alt="card.alt || `Pokemon Card ${index + 1}`"
      :card-id="`${rowId}-${index}`"
      :delay="index * staggerDelay"
      :eager-load="eagerLoad"
      @loaded="handleCardLoaded"
      @error="handleCardError"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import PokemonCard from './PokemonCard.vue'

export default {
  name: 'CardRow',
  
  components: {
    PokemonCard
  },
  
  props: {
    // Unique identifier for this row
    rowId: {
      type: [String, Number],
      required: true
    },
    
    // Array of card data objects
    cards: {
      type: Array,
      required: true,
      validator: (cards) => {
        // Each card must have at least a src property
        return cards.every(card => card.src)
      }
    },
    
    // Y position for scrolling animation
    yPosition: {
      type: Number,
      default: 0
    },
    
    // Whether row is currently visible
    isVisible: {
      type: Boolean,
      default: true
    },
    
    // Z-index for 3D layering
    zIndex: {
      type: Number,
      default: 0
    },
    
    // Stagger delay between cards in milliseconds
    staggerDelay: {
      type: Number,
      default: 50
    },
    
    // Whether to eager load all cards in this row
    eagerLoad: {
      type: Boolean,
      default: false
    },
    
    // Opacity for fade-in effect
    opacity: {
      type: Number,
      default: 1
    },
    
    // Scale for zoom effect during fade-in
    scale: {
      type: Number,
      default: 1
    },
    
    // Rotation around X axis for 3D effect
    rotateX: {
      type: Number,
      default: 35
    }
  },
  
  emits: ['row-loaded', 'card-loaded', 'card-error'],
  
  setup(props, { emit }) {
    // Track which cards have loaded
    const loadedCards = ref(new Set())
    const rowElement = ref(null)
    
    /**
     * Check if all cards in the row have loaded
     */
    const allCardsLoaded = computed(() => {
      return loadedCards.value.size === props.cards.length
    })
    
    /**
     * Computed styles for the row
     * Handles 3D positioning and smooth bezier curve transforms
     * Includes dynamic rotation for facing forward or perspective view
     */
    const rowStyles = computed(() => ({
      '--y-position': `${props.yPosition}px`,
      '--opacity': props.opacity !== undefined ? props.opacity : (props.isVisible ? 1 : 0),
      '--scale': props.scale || 1,
      '--rotate-x': `${props.rotateX}deg`,
      transform: `translateY(var(--y-position)) rotateX(var(--rotate-x)) scale(var(--scale))`,
      opacity: 'var(--opacity)',
      zIndex: props.zIndex, // Use z-index directly for proper layering
      transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
      transformOrigin: 'center top'
    }))
    
    /**
     * Handle individual card load event
     */
    const handleCardLoaded = (cardId) => {
      loadedCards.value.add(cardId)
      emit('card-loaded', { rowId: props.rowId, cardId })
      
      // Check if all cards are loaded
      if (allCardsLoaded.value) {
        emit('row-loaded', props.rowId)
      }
    }
    
    /**
     * Handle card loading error
     */
    const handleCardError = ({ cardId, error }) => {
      console.warn(`Card error in row ${props.rowId}:`, cardId, error)
      // Still mark as loaded to not block row completion
      loadedCards.value.add(cardId)
      emit('card-error', { rowId: props.rowId, cardId, error })
      
      // Check if all cards are processed
      if (allCardsLoaded.value) {
        emit('row-loaded', props.rowId)
      }
    }
    
    /**
     * Reset loaded cards when cards prop changes
     * This happens when row is recycled with new data
     */
    watch(() => props.cards, () => {
      loadedCards.value.clear()
    }, { deep: true })
    
    /**
     * Optional: Add intersection observer for row-level visibility
     * This could trigger loading of all cards in the row
     */
    const setupRowObserver = () => {
      if (!rowElement.value || props.eagerLoad) return
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Row is visible, could trigger eager loading
            // For now, cards handle their own lazy loading
          }
        })
      }, {
        rootMargin: '100px 0px',
        threshold: 0.01
      })
      
      observer.observe(rowElement.value)
      
      return observer
    }
    
    let rowObserver = null
    
    onMounted(() => {
      rowObserver = setupRowObserver()
    })
    
    onUnmounted(() => {
      if (rowObserver) {
        rowObserver.disconnect()
      }
    })
    
    return {
      loadedCards,
      allCardsLoaded,
      rowStyles,
      rowElement,
      handleCardLoaded,
      handleCardError
    }
  }
}
</script>

<style scoped>
/**
 * Card Row Styles
 * Uses Flexbox for responsive card layout
 * Cards flow horizontally with proper spacing
 */

.card-row {
  /* Use flexbox for horizontal card layout */
  display: flex;
  
  /* Cards centered with consistent spacing */
  justify-content: center;
  align-items: center;
  
  /* No wrapping - keep cards in a single row */
  flex-wrap: nowrap;
  
  /* Small gap between cards for clean look */
  gap: 4px;
  
  /* Row positioning */
  position: absolute;
  width: 100%;
  height: var(--row-height, 140px);
  left: 0;
  
  /* 3D transforms */
  transform-style: preserve-3d;
  
  /* Smooth transitions */
  transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  /* Minimal padding */
  padding: 0 10px;
  box-sizing: border-box;
  
  /* Show all content */
  overflow: visible;
  
  /* Ensure row is fully opaque */
  opacity: 1;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .card-row {
    --row-height: 110px;
    gap: 6px;
    padding: 0 10px;
  }
}

@media (min-width: 641px) and (max-width: 1024px) {
  .card-row {
    --row-height: 125px;
    gap: 8px;
    padding: 0 15px;
  }
}

@media (min-width: 1025px) and (max-width: 1440px) {
  .card-row {
    --row-height: 140px;
    gap: 10px;
    padding: 0 20px;
  }
}

@media (min-width: 1441px) {
  .card-row {
    --row-height: 145px;
    gap: 12px;
  }
}

/* Ultra-wide displays */
@media (min-width: 1920px) {
  .card-row {
    --row-height: 150px;
    gap: 14px;
    padding: 0 30px;
  }
}

/* Visibility states */
.card-row.is-visible {
  opacity: 1;
}

.card-row:not(.is-visible) {
  opacity: 0;
  pointer-events: none;
}

/* Loading state indicator */
.card-row.is-loaded {
  /* Could add special effects when all cards are loaded */
}

/* Performance optimizations */
.card-row {
  will-change: transform, opacity;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}

/* Debug mode - uncomment to see row boundaries */
/*
.card-row {
  border: 1px solid rgba(255, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.02);
}
*/
</style>